import React, { useEffect, useRef } from "react";
import { useRouter } from "next/compat/router";
import { Box, Button, Heading, Text, Modal } from "@cruk/cruk-react-components";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import { useModalContext } from "@fwa/src/contexts/ModalContext";
import { logOut } from "@fwa/src/services/auth";
import { isBrowser } from "@fwa/src/utils/browserUtils";
import {
  redirectStateFromUrl,
  urlParamsObjectClearAmParams,
} from "@fwa/src/utils/urlUtils";

import { ModalNotLoggedIn } from "@fwa/src/components/ModalNotLoggedIn";
import { ModalWrongLogIn } from "@fwa/src/components/ModalWrongLogIn";

import sharedStyles from "@fwa/src/components/styles.module.css";

import type { FundraisingPageType } from "@fwa/src/types";
const modalPartyPopper = "/assets/images/img/party-popper.svg";

// we should probably have an AM route that we use and the redirect the user to the page from there
// currently we identify the AM journey by the presence of the signature query string, which is added by AM when redirecting to the FWA and feels quite brittle
export const ActivityManagementJourneyModalManager = ({
  page,
}: {
  page: FundraisingPageType;
}) => {
  const { trackEventGtm, trackError } = useTracking();
  const [modalState, setModalState] = useModalContext();
  const [fundraiserState, setFundraiserState] = useFundraiserContext();
  const { loggedInStatus, fundraiser } = fundraiserState;
  const router = useRouter();

  const isDismissing = useRef(false);

  useEffect(() => {
    if (
      !page ||
      !router ||
      !isBrowser ||
      !trackEventGtm ||
      loggedInStatus === "unknown" ||
      modalState ||
      isDismissing.current
    ) {
      return;
    }

    // First lets check we are in the AM journey by looking at what we have in query param
    const { amMessageBody, amMessageHeader, isAmJourney } =
      redirectStateFromUrl(window.location.href);

    if (!isAmJourney) {
      return;
    }

    // lets get all information to figure out what modal we need to show. We have 3 scenarios:
    // 1. Logged out user - show ModalNotLoggedIn which prompts user to log in or sign up and then redirects to the page
    // 2. Logged in user but with the wrong account - show ModalWrongLogIn which prompts user to log out and then redirects to the page
    // 3. Logged in user with the correct account - show Modal with a success message about signing up to the activity and a CTA to explore their page
    const isLoggedOut = loggedInStatus === "loggedOut";
    const isNewUser = !page.fundraiser.isActivated || false;
    const defaultHeader = `Congratulations! You're now signed up.`;
    const defaultBody = `Personalise your page to let everyone know why you're raising money and share it with your friends and family to encourage donations. Why not be the first to add a donation to your page? It could encourage others to do the same!`;
    const header = amMessageHeader || defaultHeader;
    const body = amMessageBody || defaultBody;
    // Lets check the correct fundraiser is logged in
    const loggedInWithIncorrectAccountAndFundraiser =
      loggedInStatus === "loggedIn" &&
      page?.fundraiser &&
      fundraiser &&
      fundraiser?.uniqueId !== page.fundraiser.uniqueId;
    const loggedInWithIncorrectAccountNoFundraiserFound =
      loggedInStatus === "loggedIn" && !fundraiser;
    const isCorrectLogInForRegistration =
      !loggedInWithIncorrectAccountNoFundraiserFound &&
      !loggedInWithIncorrectAccountAndFundraiser;

    const dismissModal = () => {
      // flag to stop infinite loop when dismiss triggers infinite loop when we update the query string
      isDismissing.current = true;
      router
        ?.push(
          {
            // clear the AM params from the query string when we dismiss the modal to exit AM journey
            query: urlParamsObjectClearAmParams(router.query),
          },
          undefined,
          {
            shallow: true,
          },
        )
        .then(async () => {
          if (loggedInWithIncorrectAccountNoFundraiserFound) {
            await logOut();
            setFundraiserState({
              oauthUser: null,
              fundraiser: null,
              loggedInStatus: "loggedOut",
            });
            router.reload();
          }
          setModalState(null);
          return;
        })
        .catch((error) => {
          trackError(error, {
            component: "ActivityManagementModalForPage",
            function: "dismissModal",
          });
        });
    };

    const modalType: "loggedOut" | "wrongLogIn" | "success" = isLoggedOut
      ? "loggedOut"
      : !isCorrectLogInForRegistration
        ? "wrongLogIn"
        : "success";

    switch (modalType) {
      case "loggedOut":
        setModalState(
          <ModalNotLoggedIn
            closeFunction={dismissModal}
            isNewUser={isNewUser}
            modalName="Activity signup modal not logged in"
            componentName="am-modal-not-logged-in"
          />,
        );
        break;
      case "wrongLogIn":
        setModalState(
          <ModalWrongLogIn
            closeFunction={dismissModal}
            modalName="Activity signup modal wrong log in"
            componentName="am-modal-wrong-log-in"
          />,
        );
        break;
      default:
        // Lets fire the GTM event to denote an AM conversion
        trackEventGtm({
          event: "am_signup_confirmation",
          activityName: page.activityName,
        });
        setModalState(
          <Modal
            closeFunction={dismissModal}
            modalName="Activity signup modal"
            showCloseButton
            data-component="am-modal"
            startOpen
          >
            <div
              className="spacing-props"
              data-margin-top="xl"
              data-component="am-modal"
            >
              <Heading textAlign="center">{header}</Heading>
              <img
                className="spacing-props"
                data-margin-bottom="s"
                data-margin-top="none"
                data-margin-horizontal="auto"
                alt=""
                width="90px"
                height="90px"
                style={{
                  display: "block",
                  width: "90px",
                }}
                src={modalPartyPopper}
              />
              <Text textAlign="center">{body}</Text>
              <Box marginBottom="xs">
                <div className={sharedStyles["row-center"]}>
                  <Button
                    type="button"
                    appearance={"primary"}
                    onClick={() => {
                      dismissModal();
                    }}
                    data-cta-type="dismiss-modal"
                  >
                    Explore my page
                  </Button>
                </div>
              </Box>
            </div>
          </Modal>,
        );
        break;
    }
  }, [
    loggedInStatus,
    router,
    setModalState,
    page,
    fundraiser,
    setFundraiserState,
    trackEventGtm,
    trackError,
    modalState,
  ]);

  return null;
};

export default ActivityManagementJourneyModalManager;
